/* eslint-disable @trello/no-module-logic */
import { OperationToQuickloadUrl } from './operation-to-quickload-url.generated';

type Cache = 'Apollo' | 'ModelCache';
type Operation = keyof typeof OperationToQuickloadUrl;

const deferred = <T>() => {
  let resolve!: (value: T | PromiseLike<T>) => void;
  let reject!: (reason?: string) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });

  return {
    resolve,
    reject,
    promise,
  };
};

/**
 * Factory that signals when a cache has a specified query synced. For example,
 * MemberHeader is the main query loaded. To see when it was loaded to the apollo
 * cache you can use await cacheFactory.waitForQueryHydratedTo('MemberHeader', 'Apollo')
 *
 * To mark a query as complete, you use the markQueryHydratedFor method. This is called from
 * loadApiDataFromQuickload, as well as hydrateApolloCacheFromQuickloadResults
 */
export class CacheFactory {
  queryToPromise: {
    [cache in Cache]: {
      [key: Operation]: ReturnType<typeof deferred>;
    };
  };

  constructor() {
    const buildHash = () =>
      Object.keys(OperationToQuickloadUrl).reduce((hash, operationName) => {
        return {
          ...hash,
          [operationName]: deferred(),
        };
      }, {});

    this.queryToPromise = {
      Apollo: buildHash(),
      ModelCache: buildHash(),
    };
  }

  markQueryHydratedFor(query: Operation, cache: Cache) {
    this.queryToPromise[cache][query].resolve(null);
    return this;
  }

  waitForQueryHydratedTo(query: Operation, cache: Cache) {
    return this.queryToPromise[cache][query].promise;
  }
}

export const cacheFactory = new CacheFactory();
